import React from "react";
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../AddSite/StepsStepper";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useAudioRecorder } from "../../common/TalkDown/useAudioRecorder";
import { AssignSitesCamerasPicker } from "./AssignSitesCamerasPicker";
import {
  fetchTalkDownAssignSites,
  fetchTalkDownParentGroups,
  TalkDownParentGroup,
} from "./talkDownAssignFetchers";
import { useTalkDownMessagesStore } from "./talkDownMessagesStore";
import {
  formatTalkDownDuration,
  TalkDownAssignSite,
  TalkDownAssignment,
} from "./talkDownMessageTypes";

const STEPS = ["Record", "Assign", "Review"];

export const AddTalkDownMessageWizard = ({
  open,
  onClose,
  recordedBy = "You",
  onSaved,
  initialGroupId,
  defaultAssignment,
}: {
  open: boolean;
  onClose: () => void;
  recordedBy?: string;
  /** Called after a message is created (e.g. refresh Alerts camera picker). */
  onSaved?: () => void;
  /** Prefer this parent group when the dialog opens (e.g. Alerts filter). */
  initialGroupId?: string | number | null;
  /**
   * When set (Alerts record flow), used if the user saves without assignments
   * so the message appears in that camera's picker immediately.
   */
  defaultAssignment?: TalkDownAssignment | null;
}) => {
  const addMessage = useTalkDownMessagesStore((s) => s.addMessage);
  const [activeStep, setActiveStep] = React.useState(0);
  const [name, setName] = React.useState("");
  const [parentGroups, setParentGroups] = React.useState<TalkDownParentGroup[]>(
    []
  );
  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [groupsError, setGroupsError] = React.useState<string | null>(null);
  const [groupId, setGroupId] = React.useState<string | number>("");
  const [assignSites, setAssignSites] = React.useState<TalkDownAssignSite[]>(
    []
  );
  const [assignSitesLoading, setAssignSitesLoading] = React.useState(false);
  const [assignSitesError, setAssignSitesError] = React.useState<string | null>(
    null
  );
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = React.useState(0);
  const [assignments, setAssignments] = React.useState<TalkDownAssignment[]>(
    []
  );
  const [assignSearch, setAssignSearch] = React.useState("");
  const [recordError, setRecordError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const elapsedRef = React.useRef(0);
  const loadedAssignGroupRef = React.useRef<string | null>(null);

  const revokeAudio = React.useCallback((url: string | null) => {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
  }, []);

  const setNewAudio = React.useCallback(
    (url: string | null, seconds: number) => {
      setAudioUrl((prev) => {
        if (prev && prev !== url) revokeAudio(prev);
        return url;
      });
      setDurationSeconds(seconds);
    },
    [revokeAudio]
  );

  const recorder = useAudioRecorder({
    maxSeconds: 30,
    onRecorded: (blob) => {
      const url = URL.createObjectURL(blob);
      setNewAudio(url, Math.max(1, Math.min(30, elapsedRef.current || 1)));
    },
  });

  React.useEffect(() => {
    elapsedRef.current = recorder.elapsedSeconds;
  }, [recorder.elapsedSeconds]);

  const handleStopRecord = () => {
    elapsedRef.current = recorder.elapsedSeconds || elapsedRef.current || 1;
    recorder.stop();
  };

  React.useEffect(() => {
    if (recorder.error) setRecordError(recorder.error);
  }, [recorder.error]);

  React.useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setName("");
    setGroupId("");
    setParentGroups([]);
    setGroupsError(null);
    setAssignSites([]);
    setAssignSitesError(null);
    setAssignSitesLoading(false);
    loadedAssignGroupRef.current = null;
    setNewAudio(null, 0);
    setAssignments([]);
    setAssignSearch("");
    setRecordError(null);
    setSaveError(null);
    setSaving(false);

    let cancelled = false;
    setGroupsLoading(true);
    fetchTalkDownParentGroups()
      .then((groups) => {
        if (cancelled) return;
        setParentGroups(groups);
        if (!groups.length) return;
        const preferred =
          initialGroupId != null && initialGroupId !== ""
            ? groups.find((g) => String(g.id) === String(initialGroupId))
            : undefined;
        setGroupId(preferred ? preferred.id : groups[0].id);
      })
      .catch(() => {
        if (cancelled) return;
        setGroupsError("Could not load groups.");
        setParentGroups([]);
      })
      .finally(() => {
        if (!cancelled) setGroupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialGroupId, setNewAudio]);

  React.useEffect(() => {
    if (!open || activeStep !== 1 || groupId === "") return;
    const key = String(groupId);
    if (loadedAssignGroupRef.current === key) return;

    let cancelled = false;
    setAssignSitesLoading(true);
    setAssignSitesError(null);
    fetchTalkDownAssignSites(groupId)
      .then((sites) => {
        if (cancelled) return;
        setAssignSites(sites);
        loadedAssignGroupRef.current = key;
      })
      .catch(() => {
        if (cancelled) return;
        setAssignSites([]);
        setAssignSitesError("Could not load sites for this group.");
        loadedAssignGroupRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setAssignSitesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, activeStep, groupId]);

  if (!open) return null;

  const selectedGroup =
    parentGroups.find((g) => String(g.id) === String(groupId)) ?? null;
  const groupName = selectedGroup?.name || "Group";

  const canNextFromRecord =
    Boolean(name.trim()) &&
    Boolean(audioUrl) &&
    durationSeconds > 0 &&
    groupId !== "" &&
    !groupsLoading;

  const resolveAssignments = (
    withAssignments: TalkDownAssignment[]
  ): TalkDownAssignment[] => {
    if (withAssignments.length) return withAssignments;
    if (defaultAssignment) return [defaultAssignment];
    return [];
  };

  const save = async (withAssignments: TalkDownAssignment[]) => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await addMessage({
        name: name.trim(),
        groupId,
        groupName,
        durationSeconds,
        recordedBy,
        audioUrl,
        assignments: resolveAssignments(withAssignments),
      });
      onSaved?.();
      onClose();
    } catch {
      setSaveError("Could not save message. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          fontFamily: "Mulish",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          fontWeight: 800,
          color: "#001943",
          pr: 6,
        }}
      >
        New talk down message
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        <div className="mb-4">
          <ThemeProvider theme={theme}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </ThemeProvider>
        </div>

        <div className="font-mulish text-actuate-blue">
            {activeStep === 0 && (
              <div className="space-y-4">
                <Autocomplete
                  fullWidth
                  size="small"
                  options={parentGroups}
                  loading={groupsLoading}
                  disabled={groupsLoading || !parentGroups.length}
                  value={selectedGroup}
                  onChange={(_, value) => {
                    if (!value) return;
                    setGroupId(value.id);
                    setAssignments([]);
                    setAssignSites([]);
                    loadedAssignGroupRef.current = null;
                  }}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                  disableClearable={Boolean(selectedGroup)}
                  sx={{
                    fontFamily: "Mulish",
                    "& .MuiAutocomplete-inputRoot": { fontFamily: "Mulish" },
                  }}
                  ListboxProps={{
                    sx: {
                      fontFamily: "Mulish",
                      "& .MuiAutocomplete-option": {
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    },
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={String(option.id)} title={option.name}>
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Group"
                      size="small"
                      autoComplete="off"
                      sx={{ fontFamily: "Mulish" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {groupsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {groupsError && (
                  <p className="text-[13px] text-[#B3261E]">{groupsError}</p>
                )}
                {!groupsLoading && !groupsError && !parentGroups.length && (
                  <p className="text-[13px] text-gray-500">
                    No parent groups available for your account.
                  </p>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Message name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ fontFamily: "Mulish" }}
                />
                <div>
                  <p className="mb-1 text-[14px] font-semibold">
                    Recording{" "}
                    <span className="font-normal text-gray-500">
                      · max {formatTalkDownDuration(30)}
                    </span>
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-white ${
                        recorder.recording
                          ? "bg-[#B3261E]"
                          : "bg-actuate-orange"
                      }`}
                      aria-label={
                        recorder.recording ? "Stop recording" : "Start recording"
                      }
                      onPointerDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        setRecordError(null);
                        recorder.clearError();
                        recorder.start();
                      }}
                      onPointerUp={handleStopRecord}
                      onPointerLeave={() => {
                        if (recorder.recording) handleStopRecord();
                      }}
                      style={{ touchAction: "none" }}
                    >
                      <span className="text-[12px] font-bold">
                        {recorder.recording
                          ? formatTalkDownDuration(recorder.elapsedSeconds)
                          : "REC"}
                      </span>
                    </button>
                    {audioUrl && (
                      <audio
                        controls
                        src={audioUrl}
                        className="h-10 flex-1"
                        data-testid="talk-down-message-preview"
                      />
                    )}
                  </div>
                  <p className="mt-2 text-[12px] text-gray-500">
                    {recorder.recording
                      ? "Release to stop. You can play it back before assigning."
                      : "Hold REC to record, or upload a file below. Hold again to replace."}
                  </p>
                  {recordError && (
                    <p className="mt-2 text-[13px] text-[#B3261E]">
                      {recordError}
                    </p>
                  )}
                </div>
                <div>
                  <p className="mb-1 text-[14px] font-semibold">
                    Or upload an existing audio file
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/mpeg,audio/wav,.mp3,.wav"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const url = URL.createObjectURL(file);
                      const audio = new Audio(url);
                      audio.addEventListener("loadedmetadata", () => {
                        const secs = Number.isFinite(audio.duration)
                          ? Math.min(30, Math.max(1, Math.round(audio.duration)))
                          : 1;
                        setNewAudio(url, secs);
                      });
                      audio.addEventListener("error", () => {
                        setNewAudio(url, 1);
                      });
                    }}
                  />
                  <OrangeTextButton
                    text="Browse (.mp3, .wav)"
                    disabled={false}
                    onClick={() => fileInputRef.current?.click()}
                  />
                </div>
              </div>
            )}

            {activeStep === 1 && (
              <div>
                <p className="mb-1 text-[15px] font-semibold">
                  {name.trim() || "Untitled"} · {groupName}
                </p>
                <p className="mb-4 text-[13px] text-gray-500">
                  {formatTalkDownDuration(durationSeconds)} ·{" "}
                  <button
                    type="button"
                    className="text-actuate-orange underline"
                    onClick={() => setActiveStep(0)}
                  >
                    Re-record
                  </button>
                </p>
                <p className="mb-3 text-[14px]">
                  Where should operators be able to play this message?
                </p>
                {assignSitesLoading && (
                  <div className="mb-3 flex items-center gap-3 text-[13px] text-gray-500">
                    <CircularProgress size={22} sx={{ color: "#FF8400" }} />
                    <span>Loading sites and cameras…</span>
                  </div>
                )}
                {assignSitesError && (
                  <p className="mb-3 text-[13px] text-[#B3261E]">
                    {assignSitesError}
                  </p>
                )}
                {!assignSitesLoading && !assignSitesError && (
                  <AssignSitesCamerasPicker
                    sites={assignSites}
                    search={assignSearch}
                    onSearchChange={setAssignSearch}
                    assignments={assignments}
                    onChange={setAssignments}
                    searchPlaceholder={`Search sites and cameras in ${groupName}`}
                  />
                )}
                <p className="mt-3 text-[12px] text-gray-500">
                  You can skip this and assign later from the message.
                </p>
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-3 text-[14px]">
                <p>
                  <span className="font-semibold">Name:</span> {name.trim()}
                </p>
                <p>
                  <span className="font-semibold">Group:</span> {groupName}
                </p>
                <p>
                  <span className="font-semibold">Length:</span>{" "}
                  {formatTalkDownDuration(durationSeconds)}
                </p>
                <p>
                  <span className="font-semibold">Assignments:</span>{" "}
                  {resolveAssignments(assignments).length
                    ? `${resolveAssignments(assignments).length} site(s)`
                    : "None (unassigned)"}
                </p>
                {audioUrl && (
                  <audio controls src={audioUrl} className="w-full" />
                )}
              </div>
            )}
            {saveError && (
              <p className="mt-3 text-[13px] text-[#B3261E]">{saveError}</p>
            )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: "space-between",
        }}
      >
        {activeStep === 0 ? (
          <>
            <OrangeTextButton text="Cancel" disabled={saving} onClick={onClose} />
            <OrangeTextButton
              text="Next: Assign"
              disabled={!canNextFromRecord || saving}
              onClick={() => setActiveStep(1)}
            />
          </>
        ) : activeStep === 1 ? (
          <>
            <OrangeTextButton
              text="Back"
              disabled={saving}
              onClick={() => setActiveStep(0)}
            />
            <div className="flex gap-3">
              <OrangeTextButton
                text="Skip for now"
                disabled={saving}
                onClick={() => {
                  setAssignments([]);
                  setActiveStep(2);
                }}
              />
              <OrangeTextButton
                text={saving ? "Saving…" : "Save message"}
                disabled={saving}
                onClick={() => save(assignments)}
              />
            </div>
          </>
        ) : (
          <>
            <OrangeTextButton
              text="Back"
              disabled={saving}
              onClick={() => setActiveStep(1)}
            />
            <OrangeTextButton
              text={saving ? "Saving…" : "Save message"}
              disabled={saving}
              onClick={() => save(assignments)}
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
